import { Injectable } from '@angular/core';
import { Portal, RouteType } from '../constants';
import { Router } from '@angular/router';
import { JwtService } from 'src/auth/services/jwt.service';
import { from, map, Observable, of, switchMap, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RouteTypeService {
  certHomeRoute = '/cert';
  leaHomeRoute = '/lea/roster';
  eppHomeRoute = '/epp/cohorts';
  lea2HomeRoute = '/lea2/home';
  userTypeSelectionRoute = '/user-type-selection';

  constructor(
    private readonly jwtService: JwtService,
    private readonly router: Router,
  ) {}

  getRouteType(): RouteType {
    const fullUrl = this.router.url;

    // With the introduction of a 'lea2/staff/credential-management-roster' route, the order
    // of the checks is important. We need to check for 'lea2' before 'cert' to ensure
    // that we correctly identify the route type for lea2 routes.
    if (fullUrl.includes('/user-type-selection')) {
      return RouteType.Primary;
    } else if (fullUrl.includes('/lea2')) {
      return RouteType.Lea2;
    } else if (fullUrl.includes('/lea')) {
      return RouteType.Lea;
    } else if (fullUrl.includes('/epp')) {
      return RouteType.Epp;
    } else if (fullUrl.includes('/cert')) {
      return RouteType.Cert;
    }

    return RouteType.Primary; // Default case
  }

  getPortalSegment(): string {
    switch (this.getRouteType()) {
      case RouteType.Cert:
        return Portal.Cert;
      case RouteType.Lea:
        return Portal.Lea;
      case RouteType.Lea2:
        return Portal.Lea2;
      case RouteType.Epp:
        return Portal.Epp;
      default:
        return Portal.Cert;
    }
  }

  navigateToUserHomePage(opts?: { forceReload: boolean }): Observable<void> {
    const options = opts ?? { forceReload: false };

    return this.jwtService.allowedPortals$.pipe(
      take(1),
      switchMap((currentAllowedPortals) => {
        const currentUrl = this.router.url;
        const targetRoute = this.getHomeRouteForPortal(currentAllowedPortals);

        if (targetRoute != currentUrl || options.forceReload) {
          return from(this.router.navigateByUrl(targetRoute)).pipe(map(() => undefined));
        }

        return of(undefined);
      }),
    );
  }

  navigateToEducatorManagement(personId: number): void {
    const currentUrl = this.router.url;
    const baseRoute = currentUrl.split('/')[1];

    switch (baseRoute) {
      case 'cert':
      case 'lea':
        this.router.navigateByUrl(`${baseRoute}/educators/${personId}`);
        break;
      case 'lea2':
        this.router.navigateByUrl(`${baseRoute}/staff/staff-details/${personId}`);
        break;
      case 'epp':
        this.router.navigateByUrl(`epp/candidates/${personId}`);
        break;
      default:
        console.error(`Unknown base route: ${baseRoute}`);
        // Handle the unknown base route case if necessary
        break;
    }
  }

  getHomeRouteForPortal(currentAllowedPortals: string[]) {
    const userType = (
      currentAllowedPortals.length === 0 || currentAllowedPortals.length > 1 ? '' : currentAllowedPortals[0]
    ).toLowerCase();

    switch (userType) {
      case 'cert':
        return this.certHomeRoute;
      case 'lea':
        return this.leaHomeRoute;
      case 'lea2':
        return this.lea2HomeRoute;
      case 'epp':
        return this.eppHomeRoute;
      default:
        return this.userTypeSelectionRoute;
    }
  }
}
